import type {
  Card,
  CardCondition,
  CardFinish,
  CardRarity,
  Product,
  ProductType,
} from "@/context/AppContext";
import type { BundleStrategyId } from "@/features/bundleEngine";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MixEntry<K extends string = string> {
  key: K;
  count: number;
}

export interface AnalyzedBundle {
  cardCount: number;
  productType: ProductType;
  bundleStrategy?: BundleStrategyId;
  /** When all cards share a single set, the set name. */
  setName?: string;
  /** Distinct set names sorted by card count desc, includes setName when only one. */
  setMix: MixEntry[];
  rarityMix: MixEntry<CardRarity>[];
  /** Finish (print-treatment) tally — Holo / Reverse Holo / Premium. Cards
   *  with `finish: undefined` are excluded; only the "shiny" subset shows. */
  finishMix: MixEntry<CardFinish>[];
  conditionMix: MixEntry<CardCondition>[];
  variantMix: MixEntry[];
  /** Pokémon TCG type tally (Fire, Water, …) derived from card.tcgTypes. */
  typeMix: MixEntry[];
  /** Series tally (Scarlet & Violet, Sword & Shield, …) from card.tcgSeries. */
  seriesMix: MixEntry[];
  /** Supertype tally (Pokémon, Trainer, Energy) from card.tcgSupertype. */
  supertypeMix: MixEntry[];
  /** Subtype tally (VMAX, ex, Rule Box, …) from card.tcgSubtypes. */
  subtypeMix: MixEntry[];
  /** Sum of marketPrice * quantity across cards (0 when unpriced). */
  marketValue: number;
  /** Highest-value distinct card names (capped at 5), only those with a positive marketPrice. */
  topNames: string[];
  /** Theme label derived from strategy/product type (e.g. "Trainer & Supporter"). Undefined when generic. */
  theme?: string;
  /**
   * "all-singles" — every card has quantity === 1 and no repeated names.
   * "duplicates"  — at least one duplicate present.
   * undefined when bundle is empty.
   */
  duplicatePolicy?: "all-singles" | "duplicates";
  /** Guaranteed-contents text (auto-derived for Mystery Pack from rarity mix; overridable). */
  guaranteedContents?: string;
  /** Exclusion text (auto-derived from absent rarities/conditions; overridable). */
  excludedContent?: string;
  /** Product target sale price when set (>0). */
  suggestedListPrice?: number;
  /** Optional shipping note override; generator falls back to a generic safe default. */
  shippingNote?: string;
  /** True when only one set is represented. False with multiple sets. Undefined when no set data. */
  isSameSet?: boolean;
}

export type DescriptionSection =
  | { kind: "paragraph"; heading?: string; text: string }
  | { kind: "bullets"; heading?: string; items: string[] };

export interface GeneratedDescription {
  title?: string;
  sections: DescriptionSection[];
  plainText: string;
}

// ─── Analyzer ─────────────────────────────────────────────────────────────────

function tally<T extends string>(
  values: Iterable<{ key: T; qty: number }>,
): MixEntry<T>[] {
  const map = new Map<T, number>();
  for (const v of values) {
    map.set(v.key, (map.get(v.key) ?? 0) + v.qty);
  }
  return [...map.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}

const STRATEGY_THEME: Partial<Record<BundleStrategyId, string>> = {
  "pokemon-trainer": "Trainer & Supporter",
  "pokemon-energy": "Energy",
  "pokemon-type": "Type-themed",
  "pokemon-series": "Series",
  "pokemon-family": "Evolution / Family",
  "same-set": "Same-set",
  "rarity-tier": "Rarity-themed",
};

const PRODUCT_TYPE_THEME: Partial<Record<ProductType, string>> = {
  "25-card Trainer Lot": "Trainer & Supporter",
  "Holo/Reverse Bundle": "Holo & Reverse Holo",
  "4x Playset": "Playset",
  "Mystery Pack": "Mystery",
  "Starter/Kid Pack": "Starter",
  "Premium Single": "Premium Single",
  "Sealed Bundle": "Sealed",
};

// ── Derivation helpers ──

/**
 * For Mystery Pack: derive a truthful guaranteed-contents summary from the rarity mix
 * (e.g. "1+ Holo card and 1+ Rare card") without revealing specific names.
 * Returns undefined when nothing can be guaranteed.
 */
function deriveGuaranteedContents(
  rarityMix: MixEntry<CardRarity>[],
  finishMix: MixEntry<CardFinish>[],
): string | undefined {
  const rcounts = new Map<CardRarity, number>();
  for (const m of rarityMix) rcounts.set(m.key, m.count);
  const fcounts = new Map<CardFinish, number>();
  for (const m of finishMix) fcounts.set(m.key, m.count);
  const guarantees: string[] = [];
  // Treatment-tier guarantees first (what makes a Mystery Pack feel premium):
  // Hit collapsed into finish=Premium under the two-axis refactor.
  const premium = fcounts.get("Premium") ?? 0;
  if (premium > 0) guarantees.push(`${premium}× Hit`);
  const holo = fcounts.get("Holo") ?? 0;
  if (holo > 0) guarantees.push(`${holo}× Holo`);
  const reverse = fcounts.get("Reverse Holo") ?? 0;
  if (reverse > 0) guarantees.push(`${reverse}× Reverse Holo`);
  // Then plain Rare pulls (without any treatment). Finish-treated cards are
  // already counted above (two-axis model: a Rare+Holo card is a Holo, not a
  // second Rare), so subtract them to avoid double-counting.
  const rare = Math.max(
    0,
    (rcounts.get("Rare") ?? 0) - premium - holo - reverse,
  );
  if (rare > 0) guarantees.push(`${rare}× Rare`);
  if (guarantees.length === 0) return undefined;
  return guarantees.join(", ");
}

/**
 * Derive an exclusion list from what is absent in the bundle, so buyers know
 * what they will NOT receive. Examples:
 *  - No "Damaged" condition cards if conditionMix lacks Damaged.
 *  - No Energy cards in a Trainer Lot when none are present.
 * Only emits when truthful. Returns undefined for the empty case.
 */
function deriveExcludedContent(
  productType: ProductType,
  rarityMix: MixEntry<CardRarity>[],
  finishMix: MixEntry<CardFinish>[],
  conditionMix: MixEntry<CardCondition>[],
): string | undefined {
  const rarities = new Set(rarityMix.map((m) => m.key));
  const finishes = new Set(finishMix.map((m) => m.key));
  const conditions = new Set(conditionMix.map((m) => m.key));
  const exclusions: string[] = [];
  if (conditions.size > 0 && !conditions.has("Damaged")) {
    exclusions.push("no Damaged-condition cards");
  }
  if (
    productType === "25-card Trainer Lot" &&
    rarities.size > 0 &&
    !rarities.has("Energy")
  ) {
    exclusions.push("no Energy cards");
  }
  if (productType === "Holo/Reverse Bundle" && rarities.size > 0) {
    // A bundle is "shiny-only" when every card either has a finish (Holo /
    // Reverse Holo / Premium) or is a Promo. Plain Common / Uncommon get
    // excluded unless they carry a finish.
    const onlyShiny =
      (rarities.size > 0 && [...rarities].every((r) => r === "Promo")) || // all promos
      ([...rarities].every(
        (r) => r === "Promo" || r === "Rare" || finishes.size > 0,
      ) &&
        !rarities.has("Common") &&
        !rarities.has("Uncommon"));
    if (onlyShiny) exclusions.push("no plain Common or Uncommon cards");
  }
  if (exclusions.length === 0) return undefined;
  return exclusions.join("; ");
}

export function analyzeBundle(
  product: Pick<
    Product,
    "productType" | "bundleStrategyId" | "targetSalePrice"
  >,
  cards: Card[],
  options?: {
    guaranteedContents?: string;
    excludedContent?: string;
    shippingNote?: string;
  },
): AnalyzedBundle {
  const cardCount = cards.reduce((s, c) => s + (c.quantity || 0), 0);
  const marketValue = cards.reduce(
    (s, c) => s + (c.marketPrice || 0) * (c.quantity || 0),
    0,
  );

  const setMix = tally(
    cards
      .filter((c) => c.setName && c.setName.trim().length > 0)
      .map((c) => ({ key: c.setName, qty: c.quantity || 0 })),
  );
  const rarityMix = tally(
    cards.map((c) => ({ key: c.rarity, qty: c.quantity || 0 })),
  );
  const finishMix = tally(
    cards
      .filter((c): c is Card & { finish: CardFinish } => Boolean(c.finish))
      .map((c) => ({ key: c.finish, qty: c.quantity || 0 })),
  );
  const conditionMix = tally(
    cards.map((c) => ({ key: c.condition, qty: c.quantity || 0 })),
  );
  const variantMix = tally(
    cards
      .filter((c) => c.variant && c.variant.trim().length > 0)
      .map((c) => ({ key: c.variant, qty: c.quantity || 0 })),
  );

  // ── Card-metadata-driven mixes (only counted when the card actually carries the tag) ──
  const typeEntries: { key: string; qty: number }[] = [];
  for (const c of cards) {
    if (!c.tcgTypes || c.tcgTypes.length === 0) continue;
    for (const t of c.tcgTypes) {
      const key = t.trim();
      if (key) typeEntries.push({ key, qty: c.quantity || 0 });
    }
  }
  const typeMix = tally(typeEntries);

  const seriesMix = tally(
    cards
      .filter((c) => c.tcgSeries && c.tcgSeries.trim().length > 0)
      .map((c) => ({ key: c.tcgSeries!.trim(), qty: c.quantity || 0 })),
  );

  const supertypeMix = tally(
    cards
      .filter((c) => c.tcgSupertype && c.tcgSupertype.trim().length > 0)
      .map((c) => ({ key: c.tcgSupertype!.trim(), qty: c.quantity || 0 })),
  );

  const subtypeEntries: { key: string; qty: number }[] = [];
  for (const c of cards) {
    if (!c.tcgSubtypes || c.tcgSubtypes.length === 0) continue;
    for (const s of c.tcgSubtypes) {
      const key = s.trim();
      if (key) subtypeEntries.push({ key, qty: c.quantity || 0 });
    }
  }
  const subtypeMix = tally(subtypeEntries);

  // Top distinct card names by total market value (price × qty), only if priced.
  const valueByName = new Map<string, number>();
  for (const c of cards) {
    const v = (c.marketPrice || 0) * (c.quantity || 0);
    if (v <= 0 || !c.name) continue;
    valueByName.set(c.name, (valueByName.get(c.name) ?? 0) + v);
  }
  const topNames = [...valueByName.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name]) => name);

  const isSameSet = setMix.length === 0 ? undefined : setMix.length === 1;
  const setName = isSameSet ? setMix[0].key : undefined;

  let duplicatePolicy: AnalyzedBundle["duplicatePolicy"] | undefined;
  if (cards.length > 0) {
    const names = cards.map((c) => c.name);
    const hasQtyDupes = cards.some((c) => (c.quantity || 0) > 1);
    const hasNameDupes = new Set(names).size !== names.length;
    duplicatePolicy =
      hasQtyDupes || hasNameDupes ? "duplicates" : "all-singles";
  }

  const theme =
    (product.bundleStrategyId && STRATEGY_THEME[product.bundleStrategyId]) ||
    PRODUCT_TYPE_THEME[product.productType];

  const suggestedListPrice =
    product.targetSalePrice && product.targetSalePrice > 0
      ? product.targetSalePrice
      : undefined;

  // Auto-derive guaranteed contents for Mystery Pack from the actual rarity mix,
  // unless the caller supplied an explicit override.
  const derivedGuaranteed =
    product.productType === "Mystery Pack"
      ? deriveGuaranteedContents(rarityMix, finishMix)
      : undefined;

  // Auto-derive exclusion list from the bundle's absent rarities/conditions.
  const derivedExclusions = deriveExcludedContent(
    product.productType,
    rarityMix,
    finishMix,
    conditionMix,
  );

  return {
    cardCount,
    productType: product.productType,
    bundleStrategy: product.bundleStrategyId,
    setName,
    setMix,
    rarityMix,
    finishMix,
    conditionMix,
    variantMix,
    typeMix,
    seriesMix,
    supertypeMix,
    subtypeMix,
    marketValue,
    topNames,
    theme,
    duplicatePolicy,
    guaranteedContents: options?.guaranteedContents ?? derivedGuaranteed,
    excludedContent: options?.excludedContent ?? derivedExclusions,
    suggestedListPrice,
    shippingNote: options?.shippingNote,
    isSameSet,
  };
}

// ─── Generator ────────────────────────────────────────────────────────────────

function pluralCards(n: number): string {
  return `${n} card${n === 1 ? "" : "s"}`;
}

function joinList(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function summarizeMix<T extends string>(mix: MixEntry<T>[], max = 4): string {
  const parts = mix.slice(0, max).map((m) => `${m.count} ${m.key}`);
  return joinList(parts);
}

/**
 * Build a truthful theme phrase from real card metadata. Returns undefined
 * when the bundle does not actually share a theme (no types, no series,
 * too many distinct types, etc.) so callers can fall back to generic copy
 * instead of inventing one.
 *
 * Priority:
 *   1. Single TCG type → "Fire-type" / "Water-type"
 *   2. Two dominant TCG types covering ≥80% of cards → "Fire- and Water-type"
 *   3. Single series → "Scarlet & Violet series"
 *   4. Otherwise undefined.
 */
function describeThemeFromCardData(a: AnalyzedBundle): string | undefined {
  const total = a.typeMix.reduce((s, m) => s + m.count, 0);
  if (total > 0) {
    if (a.typeMix.length === 1) {
      return `${a.typeMix[0].key}-type`;
    }
    if (a.typeMix.length === 2) {
      const covered = a.typeMix[0].count + a.typeMix[1].count;
      if (covered / total >= 0.8) {
        return `${a.typeMix[0].key}- and ${a.typeMix[1].key}-type`;
      }
    }
  }
  if (a.seriesMix.length === 1) {
    return `${a.seriesMix[0].key} series`;
  }
  return undefined;
}

/**
 * Compose the opening summary paragraph. Per-product-type wording differs:
 * each type leads with the trait that matters most for that buyer (volume,
 * theme, playset count, etc.) instead of a one-shape-fits-all line.
 */
function bundleSummaryParagraph(a: AnalyzedBundle): string {
  switch (a.productType) {
    case "100-card Random Lot": {
      const setHint = a.isSameSet
        ? ` All cards are from ${a.setName}.`
        : a.setMix.length > 1
          ? ` Cards are pulled from ${a.setMix.length} different sets.`
          : "";
      return `A ${pluralCards(a.cardCount)} random lot great for bulk collectors and binder fillers.${setHint}`;
    }
    case "50-card Themed Lot": {
      // Prefer card-derived themes (actual TCG types / series) over the strategy label.
      const themeText =
        describeThemeFromCardData(a) ??
        (a.theme ? a.theme.toLowerCase() : "themed");
      const setHint = a.isSameSet ? ` All from ${a.setName}.` : "";
      return `A ${pluralCards(a.cardCount)} ${themeText} lot.${setHint}`;
    }
    case "25-card Trainer Lot": {
      // Our data model only tracks rarity === "Trainer" (Item/Supporter/Stadium are
      // not separate rarities), so don't claim subtype variety we can't verify.
      const hasTrainer = a.rarityMix.some((m) => m.key === "Trainer");
      const noun = hasTrainer ? "Trainer cards" : "cards";
      return `${pluralCards(a.cardCount)} of ${noun} — ready for deck building.`;
    }
    case "Holo/Reverse Bundle": {
      const finishes = new Set(a.finishMix.map((m) => m.key));
      const hasHolo = finishes.has("Holo");
      const hasReverse = finishes.has("Reverse Holo");
      let label: string;
      if (hasHolo && hasReverse) label = "holos and reverse holos";
      else if (hasHolo) label = "holos";
      else if (hasReverse) label = "reverse holos";
      else label = "shiny cards";
      return `${pluralCards(a.cardCount)} of ${label} for collectors who want only the shiny stuff.`;
    }
    case "4x Playset": {
      // We don't preserve per-name qty here, so fall back to a generic line
      // when more than one playset is present.
      const playsets = Math.floor(a.cardCount / 4);
      if (playsets <= 1 && a.topNames.length === 1) {
        return `A 4-card playset of ${a.topNames[0]}, ready to drop straight into a deck.`;
      }
      if (playsets >= 1 && a.topNames.length > 1) {
        const featured = joinList(a.topNames.slice(0, 3));
        return `${playsets} playsets (${pluralCards(a.cardCount)} total) featuring ${featured}, ready to drop straight into a deck.`;
      }
      // Fallback: don't claim a specific card.
      return `${pluralCards(a.cardCount)} grouped as deck-ready playsets.`;
    }
    case "Mystery Pack":
      return `A ${pluralCards(a.cardCount)} mystery pack — exact contents are a surprise.`;
    case "Starter/Kid Pack":
      return `${pluralCards(a.cardCount)} put together as a friendly starter pack.`;
    case "Premium Single": {
      const lead = a.topNames[0];
      return lead ? `A premium single: ${lead}.` : `A premium single card.`;
    }
    case "Sealed Bundle": {
      // Only assert "factory-fresh" if every card we have is actually marked Sealed.
      const allSealed =
        a.rarityMix.length > 0 && a.rarityMix.every((m) => m.key === "Sealed");
      const items =
        a.cardCount === 1 ? "1 sealed item" : `${a.cardCount} sealed items`;
      return allSealed
        ? `Sealed product bundle — ${items}, factory-fresh.`
        : `Sealed product bundle — ${items}.`;
    }
    case "Custom":
    default: {
      const pieces: string[] = [pluralCards(a.cardCount)];
      const themeText =
        describeThemeFromCardData(a) ??
        (a.theme ? a.theme.toLowerCase() : undefined);
      if (themeText) pieces.push(`themed around ${themeText}`);
      if (a.setName) pieces.push(`from ${a.setName}`);
      else if (a.setMix.length > 1)
        pieces.push(`drawn from ${a.setMix.length} sets`);
      return `${pieces.join(", ")}.`;
    }
  }
}

const USE_CASE: Partial<Record<ProductType, string>> = {
  "100-card Random Lot":
    "Great for collectors building bulk, deck builders looking for filler, or anyone restarting a collection.",
  "50-card Themed Lot":
    "Perfect for themed deck building, gifting, or filling a binder section.",
  "25-card Trainer Lot":
    "Ideal for deck builders restocking on Trainer-class cards in one shot.",
  "Holo/Reverse Bundle":
    "A focused bundle for collectors chasing shiny cards to brighten up a binder.",
  "4x Playset":
    "Built for competitive play — multiple copies ready to drop straight into a deck.",
  "Mystery Pack":
    "A surprise grab pack for the thrill of opening something new.",
  "Starter/Kid Pack":
    "A friendly starter selection for new players or younger collectors.",
  "Premium Single":
    "A standout single for collectors and players who want a featured card.",
  "Sealed Bundle":
    "A sealed-product bundle for collectors who prefer unopened items.",
  Custom: "A custom-built selection put together by the seller.",
};

function rarityBreakdownItems(a: AnalyzedBundle): string[] {
  if (a.rarityMix.length === 0) return [];
  return a.rarityMix.map((m) => `${m.count} × ${m.key}`);
}

function variantBreakdownItems(a: AnalyzedBundle): string[] {
  if (a.variantMix.length === 0) return [];
  return a.variantMix.map((m) => `${m.count} × ${m.key}`);
}

function setBreakdownItems(a: AnalyzedBundle): string[] {
  if (a.setMix.length === 0) return [];
  return a.setMix.slice(0, 8).map((m) => `${m.count} from ${m.key}`);
}

function conditionDisclaimer(a: AnalyzedBundle): string | undefined {
  if (a.conditionMix.length === 0) return undefined;
  if (a.conditionMix.length === 1) {
    return `All cards are in ${a.conditionMix[0].key} condition.`;
  }
  const parts = a.conditionMix.map((m) => `${m.count} × ${m.key}`);
  return `Condition mix: ${joinList(parts)}. Sleeve and toploader recommended for any played cards.`;
}

function duplicatePolicyText(a: AnalyzedBundle): string | undefined {
  if (!a.duplicatePolicy) return undefined;
  if (a.duplicatePolicy === "all-singles") {
    return "No duplicates — every card in this bundle is unique.";
  }
  return "May contain duplicates of some cards.";
}

/** Build the structured description. Returns sections + plain text. */
export function generateBundleDescription(
  a: AnalyzedBundle,
): GeneratedDescription {
  const sections: DescriptionSection[] = [];

  if (a.cardCount === 0) {
    return {
      sections: [
        {
          kind: "paragraph",
          heading: "Bundle Summary",
          text: "Bundle contents not yet selected.",
        },
      ],
      plainText: "Bundle Summary\n\nBundle contents not yet selected.",
    };
  }

  // 1. Bundle Summary
  sections.push({
    kind: "paragraph",
    heading: "Bundle Summary",
    text: bundleSummaryParagraph(a),
  });

  // 2. What's Included
  const includedItems: string[] = [];
  includedItems.push(`${pluralCards(a.cardCount)} total`);
  if (a.setName) {
    includedItems.push(`All from ${a.setName}`);
  } else if (a.setMix.length > 1) {
    includedItems.push(`${a.setMix.length} different sets represented`);
  }
  if (a.rarityMix.length > 1) {
    includedItems.push(`Rarity mix: ${summarizeMix(a.rarityMix)}`);
  } else if (a.rarityMix.length === 1) {
    // When every card shares one finish (e.g. an all-Holo bundle), lead with the
    // treatment rather than the collapsed "Rare" rarity.
    const uniformFinish =
      a.finishMix.length === 1 && a.finishMix[0].count === a.rarityMix[0].count
        ? a.finishMix[0].key
        : null;
    includedItems.push(`All ${uniformFinish ?? a.rarityMix[0].key} rarity`);
  }
  if (a.guaranteedContents) {
    includedItems.push(`Guaranteed: ${a.guaranteedContents}`);
  }
  sections.push({
    kind: "bullets",
    heading: "What's Included",
    items: includedItems,
  });

  // 3. Highlight Cards (only when we have priced standouts).
  // Mystery Pack and Starter/Kid Pack hide highlights — surprise / generic intent.
  if (
    a.topNames.length > 0 &&
    a.productType !== "Mystery Pack" &&
    a.productType !== "Starter/Kid Pack"
  ) {
    if (a.productType === "Premium Single") {
      // Already named in summary; skip the duplicate "Featured Cards" section.
    } else if (a.productType === "4x Playset") {
      // For multi-card playsets we can't be sure topNames[0] is the playset
      // (it's only the highest-value name), so list the top names without
      // claiming any single one IS the playset.
      const text =
        a.topNames.length === 1
          ? `Playset of ${a.topNames[0]}.`
          : `Includes notable cards: ${joinList(a.topNames)}.`;
      sections.push({
        kind: "paragraph",
        heading: "Featured Cards",
        text,
      });
    } else {
      sections.push({
        kind: "paragraph",
        heading: "Featured Cards",
        text: `Notable cards include ${joinList(a.topNames)}.`,
      });
    }
  }

  // 3b. Themes / Series — emit whenever card metadata is present, listing only
  // what's actually in the bundle. Never invents a unifying theme.
  const themeBullets: string[] = [];
  if (a.typeMix.length > 0) {
    themeBullets.push(`Types: ${summarizeMix(a.typeMix, 4)}`);
  }
  if (a.seriesMix.length > 0) {
    themeBullets.push(`Series: ${summarizeMix(a.seriesMix, 3)}`);
  }
  if (a.subtypeMix.length > 0) {
    themeBullets.push(`Subtypes: ${summarizeMix(a.subtypeMix, 4)}`);
  }
  if (themeBullets.length > 0) {
    sections.push({
      kind: "bullets",
      heading: "Themes & Series",
      items: themeBullets,
    });
  }

  // 4. Rarity / Variant Breakdown — only when multi-rarity or variants present.
  if (a.rarityMix.length > 1 || a.variantMix.length > 0) {
    const items = [...rarityBreakdownItems(a), ...variantBreakdownItems(a)];
    if (items.length > 0) {
      sections.push({
        kind: "bullets",
        heading: "Rarity & Variant Breakdown",
        items,
      });
    }
  }

  // 5. Set Breakdown — only when multi-set (single-set already covered in summary).
  if (a.setMix.length > 1) {
    sections.push({
      kind: "bullets",
      heading: "Set Breakdown",
      items: setBreakdownItems(a),
    });
  } else if (a.isSameSet && a.setName) {
    sections.push({
      kind: "paragraph",
      heading: "Set",
      text: `Every card in this lot is from ${a.setName} — great for set collectors.`,
    });
  }

  // 6. Use Case (per product type)
  const useCase = USE_CASE[a.productType];
  if (useCase) {
    sections.push({ kind: "paragraph", heading: "Great For", text: useCase });
  }

  // 7. Condition Disclaimer
  const cond = conditionDisclaimer(a);
  if (cond) {
    sections.push({ kind: "paragraph", heading: "Condition", text: cond });
  }

  // 8. Duplicate Policy
  const dup = duplicatePolicyText(a);
  if (dup) {
    sections.push({ kind: "paragraph", heading: "Duplicates", text: dup });
  }

  // 9. Exclusion Rules
  if (a.excludedContent) {
    sections.push({
      kind: "paragraph",
      heading: "Excludes",
      text: `This bundle contains ${a.excludedContent}.`,
    });
  }

  // 10. Shipping
  sections.push({
    kind: "paragraph",
    heading: "Shipping",
    text:
      a.shippingNote ??
      "Cards are shipped securely in protective sleeves and a top loader, packed to arrive in the same condition they left.",
  });

  return {
    sections,
    plainText: serializeSectionsToPlainText(sections),
  };
}

/** Flatten structured sections into a plain-text body suitable for eBay's description field. */
export function serializeSectionsToPlainText(
  sections: DescriptionSection[],
): string {
  const blocks: string[] = [];
  for (const s of sections) {
    if (s.kind === "paragraph") {
      const head = s.heading ? `${s.heading}\n` : "";
      blocks.push(`${head}${s.text}`.trim());
    } else {
      const head = s.heading ? `${s.heading}\n` : "";
      const body = s.items.map((i) => `• ${i}`).join("\n");
      blocks.push(`${head}${body}`.trim());
    }
  }
  return blocks.join("\n\n").trim();
}

// ─── Convenience wrapper ──────────────────────────────────────────────────────

export function buildDescriptionForProduct(
  product: Pick<
    Product,
    "productType" | "bundleStrategyId" | "targetSalePrice"
  >,
  cards: Card[],
  options?: {
    guaranteedContents?: string;
    excludedContent?: string;
    shippingNote?: string;
  },
): GeneratedDescription {
  const analyzed = analyzeBundle(product, cards, options);
  return generateBundleDescription(analyzed);
}

// ─── Template placeholder resolution ──────────────────────────────────────────

/**
 * Expand `{{cardName}}`, `{{setName}}`, `{{condition}}`, `{{price}}`,
 * `{{variant}}`, `{{rarity}}`, `{{cardNumber}}` placeholders inside an
 * existing template body using the first packaged card as the source of card
 * fields. Lets product-level descriptions saved as templates keep working
 * after the bundle generator becomes the default. When no placeholder is
 * present the input is returned unchanged.
 *
 * `{{generated}}` is the bundle-aware escape hatch: when supplied via ctx,
 * the placeholder expands to a full multi-section auto-description (the same
 * output as the "From Cards" generator on /products/$id Go-Live). Lets users
 * wrap an auto-generated bundle body with their own intro/outro text:
 *
 *   Welcome to my store!
 *   {{generated}}
 *   Ships in a top-loader within 24h.
 */
export function resolveTemplatePlaceholders(
  body: string,
  ctx: {
    firstCard?: Pick<
      Card,
      "name" | "setName" | "variant" | "rarity" | "cardNumber" | "condition"
    >;
    condition?: string;
    /** Pre-formatted price string like "$12.50" — caller is responsible for formatting. */
    price?: string;
    /** Pre-built auto-description (from `buildDescriptionForProduct(...).plainText`).
     *  Supplied only when the caller has access to the product + packaged cards; omit
     *  for single-card flows. When absent, `{{generated}}` resolves to an empty string. */
    generated?: string;
  },
): string {
  if (!body || !body.includes("{{")) return body;
  const card = ctx.firstCard;
  return body
    .replace(/\{\{generated\}\}/g, ctx.generated ?? "")
    .replace(/\{\{cardName\}\}/g, card?.name ?? "")
    .replace(/\{\{setName\}\}/g, card?.setName ?? "")
    .replace(/\{\{condition\}\}/g, ctx.condition ?? card?.condition ?? "")
    .replace(/\{\{price\}\}/g, ctx.price ?? "")
    .replace(/\{\{variant\}\}/g, card?.variant ?? "")
    .replace(/\{\{rarity\}\}/g, card?.rarity ?? "")
    .replace(/\{\{cardNumber\}\}/g, card?.cardNumber ?? "")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** True if the given body contains at least one `{{placeholder}}`. */
export function hasTemplatePlaceholders(
  body: string | undefined | null,
): boolean {
  return !!body && /\{\{[a-zA-Z]+\}\}/.test(body);
}
